<script lang="ts">
	import { createEventDispatcher } from 'svelte'
	import { page } from '$app/stores'
	import { goto, invalidateAll } from '$app/navigation'
	import { Popover, PopoverButton, PopoverPanel } from '@rgossiaux/svelte-headlessui'
	import { CheckIcon, XIcon } from '@rgossiaux/svelte-heroicons/outline'
	import { BookmarkIcon, BadgeCheckIcon } from '@rgossiaux/svelte-heroicons/outline'
	import { createPopperActions } from 'svelte-popperjs'
	import { fade, slide } from 'svelte/transition'
	import { notification_bookmarks, newActivities } from '$lib/stores/notifications'
	import ProfilePanel from './ProfilePanel.svelte'
	import ProfilePicture from '$lib/components/PFP.svelte'
	import { clickOutside } from '$lib/actions/clickOutside'
	import NewFeatureStar from '$lib/components/NewFeatureStar.svelte'
	import { profile, user } from '$lib/stores/userData'
	
	const dispatch = createEventDispatcher()

	const [popperRef, popperContent] = createPopperActions({
		placement: 'bottom'
	})

	const extraOpts = {
		modifiers: [
			{ name: 'offset', options: { offset: [0, 8] } },
			{
				name: 'flip',
				options: {
					allowedAutoPlacements: ['right', 'left', 'top', 'bottom'],
					rootBoundary: 'viewport'
				}
			}
		]
	}

	const login = () => dispatch('login')

	const onLogout = async () => {
		console.log('Logging out')
		await goto('/')
		$user = undefined
		await fetch(`/api/auth/session`, { method: 'DELETE' })
		invalidateAll()
	}
	let popover: HTMLElement
</script>

<div class="flex items-center justify-center">
	{#if $user}
		<Popover>
			<PopoverButton>
				<div use:popperRef bind:this={popover}>
					<ProfilePicture size={0} />
				</div>
			</PopoverButton>

			<PopoverPanel let:close>
				<panel class="block sm:hidden">
					<div
						in:fade|global={{ duration: 150 }}
						out:fade|global={{ duration: 150 }}
						class="fixed z-50 inset-0 w-[100vw] h-[100vh] bg-black/60 dark:bg-black/60 sm:dark:bg-black/60"
					/>
					<slider
						class="fixed -bottom-1 -left-1 -right-2 rounded-none bg-white z-50 text-black dark:bg-gray-900 dark:text-gray-100 border dark:border-gray-700 p-2 shadow-lg text-lg rounded-t-2xl"
						use:clickOutside
						on:outclick={() => close(popover)}
						in:slide|global
						out:slide|global
					>
						<ProfilePanel on:close={() => close(popover)} on:logout={() => onLogout()} />
					</slider>
				</panel>
				<menu-window use:popperContent={extraOpts} class="hidden sm:flex flex-col space-y-2 p-2">
					<a
						href={$profile.username !== ''
							? `/users/${$profile.username}`
							: `/profile`}
						class="flex space-x-2 py-2 pr-4 justify-center items-center"
					>
						<div class="shrink-0 w-32 mt-2">
							<ProfilePicture size={2} />
						</div>
						<div class="flex flex-col text-light">
							<div class="text-lg font-bold whitespace-nowrap">
								{#if $profile.name}
									{$profile.name}
								{/if}
							</div>
							<div class="">
								{#if $profile.username}
									@{$profile.username}
								{/if}
							</div>
							<div class="text-sm hidden">
								{$profile.email}
							</div>
						</div>
						<div class="hidden">
							<CheckIcon class="h-6" />
						</div>
					</a>

					{#if $profile.accountType && !$profile.accountType.includes('paid')}
						<menu-item-title>Action</menu-item-title>
						<a href={`/upgrade`} class="menu-item flex space-x-1" on:click={() => close(popover)}>
							<div>Upgrade to premium</div>
							<BadgeCheckIcon class="h-6 text-green-500" />
						</a>
					{/if}
					<menu-item-title>Pages</menu-item-title>
					<a href={`/journals/summary`} class=" menu-item" on:click={() => close(popover)}>
						Year in review &nbsp;
						<NewFeatureStar />
					</a>
					<a href={`/updates`} class=" menu-item" on:click={() => close(popover)}>
						Updates
						{#if $user && $newActivities > 0}
							<div
								class="h-4 px-1 w-fit flex text-xs items-center justify-center rounded-full bg-red-500 text-white absolute right-1 "
							>
								{$newActivities < 99 ? $newActivities : '99+'}
							</div>
						{/if}
					</a>
					<a href={`/users/all`} class="menu-item" on:click={() => close(popover)}>
						Browse Users
					</a>
					<a
						href="/saved"
						class="relative menu-item flex space-x-1"
						on:click={() => {
							close(popover)
						}}
					>
						<div>Saved</div>
						<BookmarkIcon class="text-green-500 h-5" />
						{#if $user && $notification_bookmarks > 0}
							<span
								class="h-4 w-4 flex text-xs items-center justify-center rounded-full bg-red-500 text-white ml-2 "
							>
								{$notification_bookmarks}
							</span>
						{/if}
					</a>
					<button
						class="menu-item"
						on:click={()=>alert('Coming soon!')}
					>
					click me 
					</button>

					<menu-item-title>My Account</menu-item-title>
					<a
						href={`/profile`}
						class="menu-item"
						on:click|preventDefault|capture|stopPropagation={() => close(popover)}
					>
						Profile
					</a>

					{#if $page.data.user.role === 'ADMIN'}
						<a href="/admin" class="menu-item" on:click={() => close(popover)}> Admin Console </a>
					{/if}

					<button
						class="menu-item"
						on:click|stopPropagation|preventDefault={() => {
							console.log("logout")
							onLogout()
							close(popover)
						}}
					>
						Log out
					</button>
				</menu-window>
			</PopoverPanel>
		</Popover>
	{:else}
		<div class="flex space-x-1 items-center pb-1">
			<button
				on:click={login}
				class="bg-app-500 text-white p-2 px-3 font-medium rounded-full whitespace-nowrap active:scale-95 transition duration-200"
			>
				Login
			</button>
			<button
				on:click={() => dispatch('signup')}
				class="bg-gray-300 text-black p-2 px-3 font-medium rounded-full whitespace-nowrap active:scale-95 transition duration-200"
			>
				Sign Up
			</button>
		</div>
	{/if}
</div>
